import { clearStore } from "@faro-lotv/app-component-toolbox";
import { GUID } from "@faro-lotv/foundation";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uniq } from "es-toolkit";

export enum ToolName {
  surfaceWand = "surfaceWand",
  annotation = "annotation",
  export = "export",
  manualBlurring = "manualBlurring",
  measurement = "measurement",
  desaturateSheet = "desaturateSheet",
  opacity = "opacity",
  heatmap = "heatmap",
  clipScene = "clipScene",
  /* Creation of a new Analysis */
  analysis = "analysis",
  /* Adding labels in an Analysis */
  analysisLabel = "analysisLabel",
  /** Allows editing waypoints */
  editWaypoints = "editWaypoints",
  /** Create a report of the project */
  createPDFReport = "createReport",
  /** Create a snapshot of the project */
  createSnapshot = "createSnapshot",
  /** Screenshot tool */
  screenshot = "screenshot",
}

export enum TabType {
  /** 3d/2d project captures */
  capture = "capture",
  /** Cad model structure */
  model = "model",
  /** Project annotations */
  annotations = "annotations",
}

/**
 * State of a tool's button in the Toolbar component.
 *
 * A `disabled` or `waiting` button keeps its place in the toolbar, but does not
 * react to clicks anymore. Note that such a button is not disabled through the
 * `disabled` attribute, as that would swallow the pointer events its tooltip
 * needs, but is marked with `aria-disabled` instead.
 */
export enum ToolVisibility {
  /** The button is shown and the user can use the tool */
  visible = "visible",

  /** The button is not rendered at all */
  hidden = "hidden",

  /**
   * The button is shown dimmed and cannot be used, for example because the
   * tool needs a permission the user does not have. Shows the tooltip's
   * `disabled` text, if the tool defines one.
   */
  disabled = "disabled",

  /**
   * The button is shown dimmed with a spinner on top and cannot be used, for
   * as long as the tool is busy: loading the data it needs, like the
   * measurement tool does, or performing its task, like the screenshot tool
   * does while it creates the screenshot. Shows the tooltip's `waiting` text,
   * if the tool defines one.
   */
  waiting = "waiting",
}

/** Possible actions the user can trigger to exit an Exclusive Mode */
export enum ExclusiveModeCompletionAction {
  /** Exit the exclusive mode without applying any changes */
  back = "back",

  /** Exit the exclusive mode and apply all the pending changes */
  apply = "apply",
}

export type UIState = {
  // TODO: Evaluate this state as it seems like it's never changed (i.e. setShowSpinner is never imported)
  // See https://faro01.atlassian.net/browse/SWEB-1447
  /** Show the full screen modal spinner */
  showSpinner: boolean;

  /** The tool that is currently selected as active */
  activeTool: ToolName | null;

  /** The tool to activate when the next mode is active */
  nextModeActiveTool: ToolName | null;

  /**
   * The settings menu that is currently opened.
   * The idea is that many mutually exclusive buttons can open
   * different settings menus, but only one menu can be opened
   * at a time.
   */
  activeSettingsMenu: ToolName | null;

  /** Visibility state of the individual tools within the Toolbar component */
  toolVisibility: Record<ToolName, ToolVisibility>;

  /** Open/Collapsed state of the project overview */
  isProjectOverviewExpanded: boolean;

  /** Open/Collapsed state quick help */
  isQuickHelpOpen: boolean;

  /** Visibility (Show/Hide) of the unassigned 2D photos panel */
  isUnassigned2dPhotosPanelVisible: boolean;

  /**
   * What action to exit the exclusive mode is currently in progress
   *
   * This value will change to one of the actions that the user can trigger to exit an ExclusiveMode
   * (back or apply) when that async action starts and then return to undefined when the action finishes
   *
   * It can be used to disable ui components while the exclusive mode is committing changes to the project
   * or making other state changes.
   */
  exclusiveModeCompletionAction?: `${ExclusiveModeCompletionAction}`;

  /** IDs of the open annotations in the viewer */
  expandedAnnotationsIds: GUID[];

  /** The currently active tab in the project view */
  activeTab: TabType;
};

export type OnlyUiState = {
  ui: UIState;
};

export const initialState: Readonly<UIState> = Object.freeze({
  showSpinner: false,

  activeTool: null,

  nextModeActiveTool: null,

  activeSettingsMenu: null,

  isProjectOverviewExpanded: true,

  isQuickHelpOpen: false,

  isUnassigned2dPhotosPanelVisible: false,

  // Initially set all tools to be hidden
  toolVisibility: Object.values(ToolName).reduce(
    (visibilities: Record<string, ToolVisibility>, name) => {
      visibilities[name] = ToolVisibility.hidden;
      return visibilities;
    },
    {},
  ),

  expandedAnnotationsIds: [],

  activeTab: TabType.capture,
});

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    /**
     * Change the current state of the flag showSpinner
     *
     * @param state Current state
     * @param action True if the spinner is showed
     */
    setShowSpinner(state, action: PayloadAction<boolean>) {
      state.showSpinner = action.payload;
    },

    /**
     * Mark the given tool as active
     *
     * @param state Current state
     * @param action The name of the tool to active
     */
    activateTool(state, action: PayloadAction<ToolName>) {
      state.activeTool = action.payload;
    },

    /**
     *
     * @param state Current state
     * @param action Name of settings menu to be active
     */
    setActiveSettingsMenu(state, action: PayloadAction<ToolName | null>) {
      state.activeSettingsMenu = action.payload;
    },

    /**
     * Deactivate the currently active tool
     *
     * @param state Current state
     */
    deactivateTool(state) {
      state.activeTool = null;
    },

    /**
     * Sets a tool to activate when the next mode is active
     *
     * @param state Current state
     * @param action Name of the tool to activate when next mode is ready, or null if none
     */
    setNextModeActiveTool(state, action: PayloadAction<ToolName | null>) {
      state.nextModeActiveTool = action.payload;
    },

    /**
     * Change the visibility of a tool
     *
     * @param state Current state
     * @param action Name of the tool to change to the provided visibility
     */
    changeToolVisibility(
      state,
      action: PayloadAction<{
        name: ToolName;
        visibility: ToolVisibility;
      }>,
    ) {
      state.toolVisibility[action.payload.name] = action.payload.visibility;
    },

    /**
     * Set the visibility of the project overview
     *
     * @param state Current state
     * @param action The target state of the project overview open state
     */
    setIsProjectOverviewExpanded(state, action: PayloadAction<boolean>) {
      state.isProjectOverviewExpanded = action.payload;
    },
    /**
     * Set the visibility of the quick help
     *
     * @param state Current state
     * @param action The target state of the quick help panel in mode
     */
    setIsQuickHelpOpen(state, action: PayloadAction<boolean>) {
      state.isQuickHelpOpen = action.payload;
    },

    /**
     * Set the visibility of the unassigned 2D photos panel
     *
     * @param state Current state
     * @param action The target state of the unassigned 2D photos panel visibility
     */
    setIsUnassigned2dPhotosPanelVisible(state, action: PayloadAction<boolean>) {
      state.isUnassigned2dPhotosPanelVisible = action.payload;
    },

    /**
     * Notify what completion action for the exclusive mode is currently executing
     *
     * @param state Current state
     * @param action The new exclusive mode completion action executing, or undefined if none is executing now
     */
    setExclusiveModeCompletionAction(
      state,
      action: PayloadAction<`${ExclusiveModeCompletionAction}` | undefined>,
    ) {
      state.exclusiveModeCompletionAction = action.payload;
    },

    /**
     * Expand/collapse an annotation
     *
     * @param state Current state
     * @param action The id of the annotation and the new expansion state
     */
    setAnnotationExpansion(
      state,
      action: PayloadAction<{ id: GUID; expanded: boolean }>,
    ) {
      if (action.payload.expanded) {
        state.expandedAnnotationsIds = uniq([
          ...state.expandedAnnotationsIds,
          action.payload.id,
        ]);
      } else {
        state.expandedAnnotationsIds = state.expandedAnnotationsIds.filter(
          (id) => id !== action.payload.id,
        );
      }
    },

    /**
     * Collapse all annotations
     *
     * @param state Current state
     */
    collapseAllAnnotations(state) {
      state.expandedAnnotationsIds = [];
    },

    /**
     * Set the active tab in the project view
     *
     * @param state Current state
     * @param action The tab to set as active
     */
    setActiveTab(state, action: PayloadAction<TabType>) {
      state.activeTab = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const uiReducer = uiSlice.reducer;

export const {
  activateTool,
  setActiveSettingsMenu,
  changeToolVisibility,
  deactivateTool,
  setShowSpinner,
  setIsProjectOverviewExpanded,
  setIsQuickHelpOpen,
  setIsUnassigned2dPhotosPanelVisible,
  setExclusiveModeCompletionAction,
  setAnnotationExpansion,
  collapseAllAnnotations,
  setActiveTab,
  setNextModeActiveTool,
} = uiSlice.actions;
